/**
 * TIFF directory reader for the Cloud Optimized GeoTIFFs behind raster map layers.
 *
 * The API builds these with TILING_SCHEME=GoogleMapsCompatible, BLOCKSIZE=256,
 * ALIGNED_LEVELS=7 and WebP compression, so on the seven finest levels every 256-px
 * block is exactly one XYZ tile and its bytes are a standalone WebP file:
 * block (i, j) of overview k is tile (maxZoom - k, x0 + i, y0 + j).
 *
 * Browser-side twin of packages/maps/app/geotiff/cogDirectory.ts (DataView instead
 * of Buffer); keep the two in step.
 */

export const COG_TILE_SIZE = 256;
export const COG_ZOOM_LEVELS = 7;
export const WEB_MERCATOR_HALF_EXTENT = 20037508.342789244;
export const TIFF_COMPRESSION_WEBP = 50001;

/** Reads `length` bytes at `offset` (HTTP Range, file, or an in-memory buffer). */
export type ByteReader = (offset: number, length: number) => Promise<Uint8Array>;

export interface CogLevel {
  zoom: number;
  width: number;
  height: number;
  blocksAcross: number;
  blocksDown: number;
  /** Byte offset of each block, row-major; 0 bytes in `counts` means a sparse (empty) block */
  offsets: number[];
  counts: number[];
}

export interface CogDirectory {
  bigTiff: boolean;
  compression: number;
  tileSize: number;
  maxZoom: number;
  minZoom: number;
  /** Tile column/row of the canvas's top-left block at maxZoom */
  x0: number;
  y0: number;
  /** Full resolution first, then each overview; only the aligned COG_ZOOM_LEVELS are kept */
  levels: CogLevel[];
}

export interface CogTileBlock {
  z: number;
  x: number;
  y: number;
  offset: number;
  length: number;
}

const TAG_NEW_SUBFILE_TYPE = 254;
const TAG_IMAGE_WIDTH = 256;
const TAG_IMAGE_LENGTH = 257;
const TAG_COMPRESSION = 259;
const TAG_TILE_WIDTH = 322;
const TAG_TILE_LENGTH = 323;
const TAG_TILE_OFFSETS = 324;
const TAG_TILE_BYTE_COUNTS = 325;
const TAG_MODEL_PIXEL_SCALE = 33550;
const TAG_MODEL_TIEPOINT = 33922;

const WANTED_TAGS = new Set([
  TAG_NEW_SUBFILE_TYPE,
  TAG_IMAGE_WIDTH,
  TAG_IMAGE_LENGTH,
  TAG_COMPRESSION,
  TAG_TILE_WIDTH,
  TAG_TILE_LENGTH,
  TAG_TILE_OFFSETS,
  TAG_TILE_BYTE_COUNTS,
  TAG_MODEL_PIXEL_SCALE,
  TAG_MODEL_TIEPOINT,
]);

const TYPE_SIZES: Record<number, number> = {
  1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8, 16: 8, 17: 8, 18: 8,
};

/**
 * Tile file extension for a raster-tiles manifest format. Packs cut from a COG are
 * written as WebP; packs downloaded tile-by-tile from older layers are PNG.
 */
export const rasterTileExtension = (format: string | undefined): 'webp' | 'png' => {
  return format === 'webp' ? 'webp' : 'png';
};

export class CogFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CogFormatError';
  }
}

/** Pulls bytes through a ByteReader with a growing cache so the header costs few requests. */
class CachedReader {
  private chunks: { offset: number; data: Uint8Array }[] = [];

  constructor(private read: ByteReader, private prefetch: number) {}

  async bytes(offset: number, length: number): Promise<DataView> {
    for (const chunk of this.chunks) {
      if (offset >= chunk.offset && offset + length <= chunk.offset + chunk.data.byteLength) {
        return new DataView(chunk.data.buffer, chunk.data.byteOffset + (offset - chunk.offset), length);
      }
    }
    const data = await this.read(offset, Math.max(length, this.prefetch));
    if (data.byteLength < length) {
      throw new CogFormatError(`Short read at ${offset}: wanted ${length}, got ${data.byteLength}`);
    }
    this.chunks.push({ offset, data });
    return new DataView(data.buffer, data.byteOffset, length);
  }
}

const readValues = (view: DataView, type: number, count: number, le: boolean): number[] => {
  const values: number[] = [];
  const size = TYPE_SIZES[type];
  for (let i = 0; i < count; i++) {
    const at = i * size;
    switch (type) {
      case 1: case 7: values.push(view.getUint8(at)); break;
      case 3: values.push(view.getUint16(at, le)); break;
      case 4: values.push(view.getUint32(at, le)); break;
      case 11: values.push(view.getFloat32(at, le)); break;
      case 12: values.push(view.getFloat64(at, le)); break;
      case 16: values.push(Number(view.getBigUint64(at, le))); break;
      default: values.push(NaN);
    }
  }
  return values;
};

const readOffset = (view: DataView, at: number, bigTiff: boolean, le: boolean): number => {
  return bigTiff ? Number(view.getBigUint64(at, le)) : view.getUint32(at, le);
};

type RawIfd = Map<number, number[]>;

const parseIfds = async (reader: CachedReader): Promise<{ ifds: RawIfd[]; bigTiff: boolean }> => {
  const header = await reader.bytes(0, 16);
  const order = String.fromCharCode(header.getUint8(0), header.getUint8(1));
  if (order !== 'II' && order !== 'MM') {
    throw new CogFormatError('Not a TIFF file');
  }
  const le = order === 'II';
  const magic = header.getUint16(2, le);
  let bigTiff: boolean;
  let nextIfd: number;
  if (magic === 42) {
    bigTiff = false;
    nextIfd = header.getUint32(4, le);
  } else if (magic === 43) {
    bigTiff = true;
    nextIfd = Number(header.getBigUint64(8, le));
  } else {
    throw new CogFormatError(`Unknown TIFF magic ${magic}`);
  }

  const countSize = bigTiff ? 8 : 2;
  const entrySize = bigTiff ? 20 : 12;
  const offsetSize = bigTiff ? 8 : 4;
  const valueAt = bigTiff ? 12 : 8;

  const ifds: RawIfd[] = [];
  while (nextIfd) {
    const countView = await reader.bytes(nextIfd, countSize);
    const count = bigTiff ? Number(countView.getBigUint64(0, le)) : countView.getUint16(0, le);
    const entries = await reader.bytes(nextIfd + countSize, count * entrySize + offsetSize);
    const tags: RawIfd = new Map();
    for (let i = 0; i < count; i++) {
      const base = i * entrySize;
      const tag = entries.getUint16(base, le);
      if (!WANTED_TAGS.has(tag)) {
        continue;
      }
      const type = entries.getUint16(base + 2, le);
      const valueCount = bigTiff ? Number(entries.getBigUint64(base + 4, le)) : entries.getUint32(base + 4, le);
      const size = TYPE_SIZES[type];
      if (!size) {
        continue;
      }
      let data: DataView;
      if (size * valueCount <= offsetSize) {
        data = new DataView(entries.buffer, entries.byteOffset + base + valueAt, size * valueCount);
      } else {
        data = await reader.bytes(readOffset(entries, base + valueAt, bigTiff, le), size * valueCount);
      }
      tags.set(tag, readValues(data, type, valueCount, le));
    }
    ifds.push(tags);
    nextIfd = readOffset(entries, count * entrySize, bigTiff, le);
  }
  return { ifds, bigTiff };
};

const single = (ifd: RawIfd, tag: number): number | undefined => ifd.get(tag)?.[0];

/** Web Mercator zoom whose 256-px tiles have this resolution (COG resolutions are exact powers of two). */
export const zoomForResolution = (metresPerPixel: number): number => {
  return Math.round(Math.log2((2 * WEB_MERCATOR_HALF_EXTENT) / (COG_TILE_SIZE * metresPerPixel)));
};

/**
 * Read the directory of a COG. Only the first (full resolution) IFD and its
 * reduced-resolution subfiles are used; mask IFDs are ignored.
 */
export const readCogDirectory = async (read: ByteReader, prefetch = 256 * 1024): Promise<CogDirectory> => {
  const reader = new CachedReader(read, prefetch);
  const { ifds, bigTiff } = await parseIfds(reader);
  if (ifds.length === 0) {
    throw new CogFormatError('TIFF has no image directory');
  }
  const full = ifds[0];
  if (single(full, TAG_TILE_WIDTH) !== COG_TILE_SIZE || single(full, TAG_TILE_LENGTH) !== COG_TILE_SIZE) {
    throw new CogFormatError(`Expected ${COG_TILE_SIZE}px blocks`);
  }
  const compression = single(full, TAG_COMPRESSION) ?? 1;
  const scale = full.get(TAG_MODEL_PIXEL_SCALE);
  const tiepoint = full.get(TAG_MODEL_TIEPOINT);
  if (!scale || !tiepoint || tiepoint.length < 6) {
    throw new CogFormatError('COG is missing its georeferencing tags');
  }
  const resolution = scale[0];
  const originX = tiepoint[3] - tiepoint[0] * resolution;
  const originY = tiepoint[4] + tiepoint[1] * resolution;
  const maxZoom = zoomForResolution(resolution);
  const tileMetres = (2 * WEB_MERCATOR_HALF_EXTENT) / 2 ** maxZoom;
  const x0 = Math.round((originX + WEB_MERCATOR_HALF_EXTENT) / tileMetres);
  const y0 = Math.round((WEB_MERCATOR_HALF_EXTENT - originY) / tileMetres);

  const levels: CogLevel[] = [];
  const imageIfds = ifds.filter((ifd, index) => index === 0 || ((single(ifd, TAG_NEW_SUBFILE_TYPE) ?? 0) & 1) === 1);
  for (let k = 0; k < imageIfds.length && k < COG_ZOOM_LEVELS; k++) {
    const ifd = imageIfds[k];
    const width = single(ifd, TAG_IMAGE_WIDTH);
    const height = single(ifd, TAG_IMAGE_LENGTH);
    const offsets = ifd.get(TAG_TILE_OFFSETS);
    const counts = ifd.get(TAG_TILE_BYTE_COUNTS);
    if (!width || !height || !offsets || !counts) {
      throw new CogFormatError(`Overview ${k} is missing tile tags`);
    }
    if (single(ifd, TAG_TILE_WIDTH) !== COG_TILE_SIZE) {
      throw new CogFormatError(`Overview ${k} does not use ${COG_TILE_SIZE}px blocks`);
    }
    levels.push({
      zoom: maxZoom - k,
      width,
      height,
      blocksAcross: Math.ceil(width / COG_TILE_SIZE),
      blocksDown: Math.ceil(height / COG_TILE_SIZE),
      offsets,
      counts,
    });
  }

  return {
    bigTiff,
    compression,
    tileSize: COG_TILE_SIZE,
    maxZoom,
    minZoom: maxZoom - (levels.length - 1),
    x0,
    y0,
    levels,
  };
};

/** Every block that has bytes, with its tile address, sorted by file offset. */
export const listTileBlocks = (dir: CogDirectory): CogTileBlock[] => {
  const blocks: CogTileBlock[] = [];
  dir.levels.forEach((level, k) => {
    const x0 = dir.x0 >> k;
    const y0 = dir.y0 >> k;
    for (let index = 0; index < level.counts.length; index++) {
      const length = level.counts[index];
      if (length) {
        blocks.push({
          z: level.zoom,
          x: x0 + (index % level.blocksAcross),
          y: y0 + Math.floor(index / level.blocksAcross),
          offset: level.offsets[index],
          length,
        });
      }
    }
  });
  blocks.sort((a, b) => a.offset - b.offset);
  return blocks;
};

/**
 * Zooms coarser than the COG's levels are still shown so the layer stays visible
 * zoomed right out. The maps service composites them on the fly; the offline pack
 * builds them level by level from the level above, each tile being its four
 * children at z+1 drawn into 128-px quadrants.
 */
export const UNDERZOOM_MIN_ZOOM = 0;

export interface UnderzoomTile {
  z: number;
  x: number;
  y: number;
  /** Tiles at z + 1 present on disk that fall inside this tile */
  children: { x: number; y: number }[];
}

/** Derived tiles below the COG's coarsest level, finest first so each level's inputs already exist. */
export const planUnderzoomTiles = (dir: CogDirectory): UnderzoomTile[] => {
  const k = dir.levels.length - 1;
  const level = dir.levels[k];
  const sx0 = dir.x0 >> k;
  const sy0 = dir.y0 >> k;
  let present: { x: number; y: number }[] = [];
  for (let index = 0; index < level.counts.length; index++) {
    if (level.counts[index]) {
      present.push({ x: sx0 + (index % level.blocksAcross), y: sy0 + Math.floor(index / level.blocksAcross) });
    }
  }
  const plan: UnderzoomTile[] = [];
  for (let z = dir.minZoom - 1; z >= UNDERZOOM_MIN_ZOOM; z--) {
    const parents = new Map<string, UnderzoomTile>();
    for (const child of present) {
      const x = child.x >> 1;
      const y = child.y >> 1;
      const key = `${x},${y}`;
      let parent = parents.get(key);
      if (!parent) {
        parent = { z, x, y, children: [] };
        parents.set(key, parent);
      }
      parent.children.push(child);
    }
    const tiles = [...parents.values()].sort((a, b) => a.y - b.y || a.x - b.x);
    plan.push(...tiles);
    present = tiles.map(({ x, y }) => ({ x, y }));
  }
  return plan;
};
